/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR444: Universal Flyer Preview — SSOT renderer.
 * CR448: Self-hydrating — fetches flyer artifact data when deal has UUID id
 *        but no flyer_artifact, so it works regardless of which hook loaded the deal.
 * CR450: Click-to-expand modal interaction (configurable via block config).
 * 
 * Resolution priority:
 * 1. snippet_url / page_url (pre-signed from hydration)
 * 2. snippet_asset_path_norm / snippet_asset_path → useSignedStorageUrl
 * 3. page_image_path_norm / page_image_path → useSignedStorageUrl
 * 4. Legacy keys (artifacts.flyer_snippet.url)
 * 5. Self-hydrate from v_deal_flyer_snippets_v2 if deal.id is UUID
 * 6. Placeholder (always visible, includes deal ID + status)
 */

import { useState, useEffect, useRef } from "react";
import { FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSignedStorageUrl } from "@/hooks/useSignedStorageUrl";
import { DealIdBadge } from "@/components/deals/DealIdBadge";
import { fetchFlyerSnippetForDeal } from "@/lib/deals/fetchFlyerSnippetForDeal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type AnyDeal = any;

function pickFirstString(...vals: any[]): string | null {
  for (const v of vals) {
    if (typeof v === "string" && v.trim().length > 0) return v;
  }
  return null;
}

/**
 * CR444: Resolve flyer URL from deal data.
 * Returns either a pre-signed URL or a raw storage path for useSignedStorageUrl.
 */
function resolveFlyerData(deal: AnyDeal): {
  preSignedUrl: string | null;
  rawStoragePath: string | null;
  source: string;
} {
  const fa = deal?.flyer_artifact ?? deal?.flyerArtifact ?? deal?.flyer ?? null;

  // 1. Pre-signed URLs (from hydration)
  const signedUrl = pickFirstString(
    fa?.snippet_url, fa?.snippetUrl,
    fa?.page_url, fa?.pageUrl,
    deal?.artifacts?.flyer_snippet?.url,
    deal?.artifacts?.flyer_snippet?.thumbnail_url,
    deal?.flyer_snippet_url,
    deal?.flyer_thumbnail_url,
  );

  if (signedUrl) {
    const source =
      (pickFirstString(fa?.snippet_url, fa?.snippetUrl) && "flyer_snippet") ||
      (pickFirstString(fa?.page_url, fa?.pageUrl) && "flyer_page") ||
      "legacy";
    return { preSignedUrl: signedUrl, rawStoragePath: null, source };
  }

  // 2. CR447: Deterministic bucket-qualified storage paths (_norm preferred)
  const snippetStoragePath = pickFirstString(
    fa?.snippet_storage_path_norm,
    fa?.snippet_storage_path,
  );
  if (snippetStoragePath) {
    return { preSignedUrl: null, rawStoragePath: snippetStoragePath, source: "snippet_storage_path" };
  }

  const pageStoragePath = pickFirstString(
    fa?.page_storage_path_norm,
    fa?.page_storage_path,
  );
  if (pageStoragePath) {
    return { preSignedUrl: null, rawStoragePath: pageStoragePath, source: "page_storage_path" };
  }

  // 3. Raw snippet path (_norm preferred, prefix for signing)
  const snippetPath = pickFirstString(
    fa?.snippet_asset_path_norm,
    fa?.snippet_asset_path,
    deal?.artifacts?.flyer_snippet?.asset_path,
  );
  if (snippetPath) {
    const prefixed = snippetPath.startsWith('flyer-snippets/') ? snippetPath : `flyer-snippets/${snippetPath}`;
    return { preSignedUrl: null, rawStoragePath: prefixed, source: "snippet_asset_path" };
  }

  // 4. Raw page image path (_norm preferred)
  const pagePath = pickFirstString(
    fa?.page_image_path_norm,
    fa?.page_image_path,
  );
  if (pagePath) {
    const prefixed = pagePath.startsWith('flyer-fetch-assets/') ? pagePath : `flyer-fetch-assets/${pagePath}`;
    return { preSignedUrl: null, rawStoragePath: prefixed, source: "page_image_path" };
  }

  return { preSignedUrl: null, rawStoragePath: null, source: "placeholder" };
}

// Re-export for external consumers
export function resolveFlyerUrl(deal: AnyDeal) {
  const { preSignedUrl, source } = resolveFlyerData(deal);
  return { url: preSignedUrl, source };
}

// CR450: Flyer interaction types
export type FlyerInteractionMode = "none" | "modal";

export interface FlyerSnippetInteractionConfig {
  interaction?: FlyerInteractionMode;
  modalTitle?: string;
  allowBackdropClose?: boolean;
}

interface FlyerPreviewProps {
  deal: AnyDeal;
  className?: string;
  aspect?: string;
  showDebugHint?: boolean;
  variant?: "card" | "details";
  /** CR450: interaction config from block config */
  interactionConfig?: FlyerSnippetInteractionConfig;
}

export function FlyerPreview({
  deal,
  className = "",
  aspect = "aspect-[4/3]",
  showDebugHint = false,
  interactionConfig,
}: FlyerPreviewProps) {
  // CR448: Self-hydration — if deal has UUID id but no flyer artifact, fetch it
  const [selfHydrated, setSelfHydrated] = useState<Record<string, any> | null>(null);
  const hydrateAttemptedRef = useRef<string | null>(null);

  const dealId = (deal as any)?.id ?? null;
  const looksUuid = typeof dealId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(dealId);

  // Check if deal already has flyer artifact data
  const existingFa = (deal as any)?.flyer_artifact ?? (deal as any)?.flyerArtifact ?? (deal as any)?.flyer ?? null;
  const hasExistingData = !!(
    existingFa?.snippet_url || existingFa?.snippetUrl ||
    existingFa?.page_url || existingFa?.pageUrl ||
    existingFa?.snippet_storage_path_norm || existingFa?.snippet_asset_path_norm ||
    existingFa?.snippet_asset_path || existingFa?.page_image_path_norm ||
    existingFa?.page_image_path ||
    (deal as any)?.artifacts?.flyer_snippet?.url
  );

  useEffect(() => {
    if (hasExistingData || !looksUuid || !dealId) return;
    if (hydrateAttemptedRef.current === dealId) return; // already attempted
    hydrateAttemptedRef.current = dealId;

    let cancelled = false;
    fetchFlyerSnippetForDeal(dealId).then((result) => {
      if (cancelled || !result) return;
      setSelfHydrated(result);
    });
    return () => { cancelled = true; };
  }, [dealId, looksUuid, hasExistingData]);

  // Use self-hydrated data as a synthetic deal overlay
  const effectiveDeal = selfHydrated && !hasExistingData
    ? { ...deal, flyer_artifact: selfHydrated }
    : deal;

  const { preSignedUrl, rawStoragePath, source } = resolveFlyerData(effectiveDeal);

  // CR444: useSignedStorageUrl handles bucket detection internally
  const { url: signedUrl } = useSignedStorageUrl(rawStoragePath);

  const finalUrl = preSignedUrl ?? signedUrl ?? null;
  const [imgError, setImgError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // CR450: interaction config
  const interaction = interactionConfig?.interaction ?? "none";
  const modalTitle = interactionConfig?.modalTitle ?? "Flyer preview";
  const allowBackdropClose = interactionConfig?.allowBackdropClose !== false;
  const canOpenModal = interaction === "modal" && !!finalUrl && !imgError;

  // CR449: Debug hint showing source + status (always visible in admin/debug)
  const debugLabel = `${source}${selfHydrated ? ' [self]' : ''}`;

  if (finalUrl && !imgError) {
    return (
      <>
        <div
          className={cn(
            "relative w-full rounded-lg overflow-hidden border bg-muted/30",
            canOpenModal && "cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow",
            aspect,
            className,
          )}
          role={canOpenModal ? "button" : undefined}
          tabIndex={canOpenModal ? 0 : undefined}
          aria-label={canOpenModal ? "View flyer preview" : undefined}
          onClick={canOpenModal ? () => setModalOpen(true) : undefined}
          onKeyDown={canOpenModal ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setModalOpen(true); } } : undefined}
        >
          <img
            src={finalUrl}
            alt="Flyer preview"
            className="w-full h-full object-contain"
            loading="lazy"
            draggable={false}
            onError={() => {
              console.warn("[CR444] FlyerPreview img failed:", finalUrl, "source:", source);
              setImgError(true);
            }}
          />
          {showDebugHint && (
            <span className="absolute bottom-1 right-1 text-[9px] font-mono bg-background/80 text-muted-foreground px-1 rounded">
              {debugLabel}
            </span>
          )}
        </div>

        {/* CR450/CR451: Expand modal with backdrop close control */}
        {canOpenModal && (
          <Dialog open={modalOpen} onOpenChange={setModalOpen}>
            <DialogContent
              className="max-w-3xl max-h-[90vh] p-2 sm:p-4"
              onPointerDownOutside={(e) => { if (!allowBackdropClose) e.preventDefault(); }}
              onInteractOutside={(e) => { if (!allowBackdropClose) e.preventDefault(); }}
            >
              <DialogHeader>
                <DialogTitle className="text-sm font-medium">{modalTitle}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-center overflow-auto max-h-[80vh]">
                <img
                  src={finalUrl}
                  alt={modalTitle}
                  className="max-w-full max-h-[75vh] object-contain rounded"
                  draggable={false}
                />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  // CR444: Placeholder — includes deal ID + flyer status
  const placeholderFa = effectiveDeal?.flyer_artifact ?? effectiveDeal?.flyerArtifact ?? null;
  const flyerStatus = placeholderFa?.status ?? placeholderFa?.flyer_asset_status ?? null;
  const placeholderDealId = (effectiveDeal as any)?.id ?? null;

  return (
    <div className={cn(
      "relative w-full rounded-lg overflow-hidden border-2 border-dashed border-muted-foreground/40 bg-muted/30 flex items-center justify-center",
      aspect,
      className,
    )}>
      <div className="flex flex-col items-center gap-2 text-muted-foreground px-3">
        <FileText className="h-10 w-10" />
        <span className="text-sm font-semibold">Flyer preview not available</span>
        <span className="text-xs text-center">
          {flyerStatus
            ? `Status: ${flyerStatus}`
            : "Snippet pending or not yet generated"}
        </span>
        {placeholderDealId && (
          <DealIdBadge dealId={placeholderDealId} className="mt-1" />
        )}
      </div>
    </div>
  );
}
